import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Camera, FileText, Sparkles, X, Plus, Image as ImageIcon, Anchor, MapPin } from "lucide-react";
import { fileToAttachment, isPdfDataUri, openPdfDataUri } from "@/lib/imageUtil";
import { CURRENCIES, FIELD_PRESETS, DEFAULT_FIELDS } from "@/lib/entryFieldConfig";
import EncounterPicker from "@/components/EncounterPicker";
import LocationCapture from "@/components/LocationCapture";
import { useAuth } from "@/contexts/AuthContext";
import { isProUser, goUpgrade } from "@/lib/plan";

const MAX_PHOTOS = 8;

/**
 * EntryModal — full create/edit form for any vessel entry.
 *
 * Field set is driven by `FIELD_PRESETS[category]` (or the `fields` fallback
 * passed in). Reference categories (Parts, Zincs) switch to a stock-tracking
 * layout. Custom-fields per category are loaded from `customFieldsByCat`.
 *
 * Props:
 *   vesselId, defaultCategory, categories, initial?, fields?,
 *   customFieldsByCat?, defaultCurrency?, onClose, onSaved
 */
export default function EntryModal({
    vesselId,
    defaultCategory,
    categories,
    initial,
    fields,
    customFieldsByCat = {},
    defaultCurrency = "USD",
    engines = [],
    trips = [],
    onClose,
    onSaved,
}) {
    const [form, setForm] = useState(() => {
        // Initial photos list: prefer the new `photos` array; fall back to the
        // legacy single `photo_data`/`photo_filename` pair on older records.
        const initPhotos = Array.isArray(initial?.photos) && initial.photos.length
            ? initial.photos.map((p) => ({ data: p.data, filename: p.filename || "" }))
            : initial?.photo_data
                ? [{ data: initial.photo_data, filename: initial.photo_filename || "" }]
                : [];
        return {
            category: initial?.category || defaultCategory,
            date: initial?.date || new Date().toISOString().slice(0, 10),
            title: initial?.title || "",
            amount: initial?.amount ?? "",
            currency: initial?.currency || defaultCurrency,
            quantity: initial?.quantity ?? "",
            unit: initial?.unit || "",
            unit_price: initial?.unit_price ?? "",
            hours_at_event: initial?.hours_at_event ?? "",
            vendor: initial?.vendor || "",
            notes: initial?.notes || "",
            status: initial?.status || "",
            due_at: initial?.due_at || "",
            due_hours: initial?.due_hours ?? "",
            oem_number: initial?.oem_number || "",
            aftermarket_number: initial?.aftermarket_number || "",
            date_to: initial?.date_to || "",
            hours_underway: initial?.hours_underway ?? "",
            distance_nm: initial?.distance_nm ?? "",
            // New: multi-engine + trip fields
            engine_id: initial?.engine_id || "",
            trip_id: initial?.trip_id || "",
            start_hours: initial?.start_hours ?? "",
            end_hours: initial?.end_hours ?? "",
            // Cruiser Contacts encountered on this log entry (Cruising Log only)
            encounter_contact_ids: Array.isArray(initial?.encounter_contact_ids) ? initial.encounter_contact_ids : [],
            // Optional GPS fix attached on save (Cruising Log only)
            lat: initial?.lat ?? null,
            lng: initial?.lng ?? null,
            place_name: initial?.place_name ?? null,
            photos: initPhotos,
            custom_data: initial?.custom_data || {},
        };
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [photoBusy, setPhotoBusy] = useState(false);
    const [lightboxIdx, setLightboxIdx] = useState(-1);
    const [ocrBusy, setOcrBusy] = useState(false);
    const [ocrNote, setOcrNote] = useState("");

    const f = FIELD_PRESETS[form.category] || fields || DEFAULT_FIELDS;
    const isRef = !!f.isReference;
    const customFields = customFieldsByCat[form.category] || [];
    // Run OCR against the first attached photo/pdf — AI extraction is Pro
    const { user } = useAuth();
    const isPro = isProUser(user);
    const canOcr = form.photos.length > 0;

    const setCustom = (key, value) => {
        setForm((s) => ({ ...s, custom_data: { ...(s.custom_data || {}), [key]: value } }));
    };

    const onPhotosPicked = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        const room = MAX_PHOTOS - form.photos.length;
        if (room <= 0) {
            setErr(`You can attach up to ${MAX_PHOTOS} photos per entry.`);
            return;
        }
        const slice = files.slice(0, room);
        setPhotoBusy(true);
        setErr("");
        try {
            const atts = await Promise.all(
                slice.map((file) => fileToAttachment(file, { maxDim: 1400, quality: 0.82 })),
            );
            setForm((s) => ({
                ...s,
                photos: [
                    ...s.photos,
                    ...atts.map((a) => ({ data: a.dataUri, filename: a.filename })),
                ],
            }));
            if (files.length > room) {
                setErr(`Only attached the first ${room} — limit is ${MAX_PHOTOS} per entry.`);
            }
        } catch (e) {
            console.warn("[entry-modal] photo read failed", e);
            setErr("Could not read one of those files. Try smaller images.");
        } finally {
            setPhotoBusy(false);
        }
    };

    const removePhoto = (idx) => {
        setForm((s) => ({ ...s, photos: s.photos.filter((_, i) => i !== idx) }));
    };

    const extractWithAi = async () => {
        if (!canOcr || ocrBusy) return;
        setOcrBusy(true);
        setOcrNote("");
        try {
            const res = await api.post("/receipts/extract", {
                data_uri: form.photos[0].data,
                hint_category: form.category,
            });
            const d = res.data || {};
            if (!d.ok) {
                setOcrNote(d.note || "Couldn't extract details. Please enter manually.");
                return;
            }
            // Merge — only fill empty fields so we never clobber user typing
            setForm((s) => {
                const next = { ...s };
                if (!s.vendor && d.vendor) next.vendor = d.vendor;
                if ((s.amount === "" || s.amount == null) && d.amount != null) next.amount = String(d.amount);
                if (!s.currency && d.currency) next.currency = d.currency;
                if (!s.date && d.date) next.date = d.date;
                if ((s.quantity === "" || s.quantity == null) && d.quantity != null) next.quantity = String(d.quantity);
                if (!s.unit && d.unit) next.unit = d.unit;
                if ((s.unit_price === "" || s.unit_price == null) && d.unit_price != null) next.unit_price = String(d.unit_price);
                if (!s.notes && d.notes) next.notes = d.notes;
                if (!s.title && d.vendor) next.title = d.vendor;
                return next;
            });
            const filledCount = ["vendor", "amount", "date", "quantity", "unit", "unit_price"].filter(
                (k) => d[k] != null && d[k] !== "",
            ).length;
            setOcrNote(`AI filled ${filledCount} field${filledCount === 1 ? "" : "s"}. Review before saving.`);
        } catch (e) {
            console.warn("[entry-modal] ocr extract failed", e);
            setOcrNote("AI extraction failed. Please enter details manually.");
        } finally {
            setOcrBusy(false);
        }
    };

    const save = async () => {
        setErr("");
        setBusy(true);
        const photosOut = (form.photos || []).map((p) => ({ data: p.data, filename: p.filename || null }));
        const payload = {
            category: form.category,
            date: isRef ? null : form.date || null,
            title: form.title || (f.isCruisingLog ? "Log Entry" : ""),
            amount: form.amount === "" ? null : parseFloat(form.amount),
            currency: form.amount === "" ? null : form.currency || defaultCurrency,
            quantity: form.quantity === "" ? null : parseFloat(form.quantity),
            // Cruising-log fuel defaults to gallons when the captain enters a
            // quantity but never touches the unit select.
            unit: form.unit || (f.isCruisingLog && form.quantity !== "" ? "gal" : null),
            unit_price: form.unit_price === "" ? null : parseFloat(form.unit_price),
            hours_at_event: isRef ? null : form.hours_at_event === "" ? null : parseFloat(form.hours_at_event),
            vendor: form.vendor || null,
            notes: form.notes || null,
            status: isRef ? null : form.status || null,
            due_at: isRef ? null : form.due_at || null,
            due_hours: isRef ? null : form.due_hours === "" ? null : parseFloat(form.due_hours),
            oem_number: form.oem_number || null,
            aftermarket_number: form.aftermarket_number || null,
            date_to: f.isCruisingLog ? (form.date_to || null) : null,
            hours_underway: f.isCruisingLog && form.hours_underway !== "" ? parseFloat(form.hours_underway) : null,
            distance_nm: f.isCruisingLog && form.distance_nm !== "" ? parseFloat(form.distance_nm) : null,
            // Trip + start/end hours apply only to Cruising Log entries
            trip_id: f.isCruisingLog ? (form.trip_id || null) : null,
            start_hours: f.isCruisingLog && form.start_hours !== "" ? parseFloat(form.start_hours) : null,
            end_hours: f.isCruisingLog && form.end_hours !== "" ? parseFloat(form.end_hours) : null,
            // Engine attribution applies to Maintenance, Fuel, Fuel Economy and Cruising Log
            engine_id: form.engine_id || null,
            // Cruiser Contacts met on this log day (Cruising Log only).
            // Backend ignores it for non-cruising-log categories anyway, but
            // we null it out explicitly to keep payloads clean.
            encounter_contact_ids: f.isCruisingLog && form.encounter_contact_ids?.length
                ? form.encounter_contact_ids
                : null,
            // GPS fix (Cruising Log only) — same gating pattern as above.
            lat: f.isCruisingLog ? (form.lat ?? null) : null,
            lng: f.isCruisingLog ? (form.lng ?? null) : null,
            place_name: f.isCruisingLog ? (form.place_name || null) : null,
            photos: photosOut.length ? photosOut : null,
            // Clear legacy fields when saving via the new path so reads don't double-up
            photo_data: null,
            photo_filename: null,
            custom_data: form.custom_data || {},
        };
        try {
            if (initial?.id) await api.patch(`/vessels/${vesselId}/entries/${initial.id}`, payload);
            else await api.post(`/vessels/${vesselId}/entries`, payload);
            onSaved();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="entry-modal"
            className="bm-modal-overlay"
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "grid", placeItems: "center", padding: 20, zIndex: 100 }}
            onClick={onClose}
        >
            <div
                className="bm-card"
                style={{ padding: 28, maxWidth: 520, width: "100%", maxHeight: "90vh", overflow: "auto" }}
                onClick={(e) => e.stopPropagation()}
            >
                <h3 className="bm-serif" style={{ fontSize: 24, margin: "0 0 18px", fontWeight: 500 }}>
                    {initial?.id ? "Edit" : "New"} {isRef ? "part" : "entry"}
                </h3>

                <div style={{ marginBottom: 12 }}>
                    <label className="bm-label">Category</label>
                    <select
                        data-testid="entry-category-select"
                        className="bm-input"
                        value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                    >
                        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                <div style={{ marginBottom: 12 }}>
                    <label className="bm-label">
                        {isRef ? "Component" : "Title"}
                        {f.isCruisingLog && <span style={{ color: "var(--bm-text-dim)", fontWeight: 400, marginLeft: 6 }}>(optional)</span>}
                    </label>
                    <input
                        data-testid="entry-title-input"
                        className="bm-input"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        required={!f.isCruisingLog}
                        placeholder={isRef ? "e.g. Main Oil Filter" : f.isCruisingLog ? "e.g. Day 1 — Skagway to Haines (auto-filled if blank)" : ""}
                    />
                </div>

                {/* Engine selector — shown for Maintenance, Fuel, Fuel Economy, and Cruising Log only when 2+ engines exist */}
                {!isRef && engines.length >= 2 && ["Maintenance", "Fuel", "Fuel Economy", "Cruising Log"].includes(form.category) && (
                    <div style={{ marginBottom: 12 }}>
                        <label className="bm-label">Engine</label>
                        <select
                            data-testid="entry-engine-select"
                            className="bm-input"
                            value={form.engine_id || ""}
                            onChange={(e) => setForm({ ...form, engine_id: e.target.value })}
                        >
                            <option value="">Whole vessel / unspecified</option>
                            {engines.map((eng) => (
                                <option key={eng.id} value={eng.id}>
                                    {eng.label}{eng.make ? ` · ${eng.make}` : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {isRef ? (
                    <ReferenceFields form={form} setForm={setForm} />
                ) : f.isCruisingLog ? (
                    <CruisingLogFields form={form} setForm={setForm} trips={trips} />
                ) : (
                    <StandardFields form={form} setForm={setForm} f={f} />
                )}

                {f.showPhoto && (
                    <PhotoSection
                        form={form}
                        photoBusy={photoBusy}
                        setLightboxIdx={setLightboxIdx}
                        onPhotosPicked={onPhotosPicked}
                        removePhoto={removePhoto}
                        canOcr={canOcr}
                        isPro={isPro}
                        ocrBusy={ocrBusy}
                        ocrNote={ocrNote}
                        extractWithAi={extractWithAi}
                    />
                )}

                {customFields.length > 0 && (
                    <CustomFieldsSection
                        category={form.category}
                        customFields={customFields}
                        customData={form.custom_data || {}}
                        setCustom={setCustom}
                    />
                )}

                <div style={{ marginBottom: 14 }}>
                    <label className="bm-label">{f.isCruisingLog ? "Anchorages & notes" : "Notes"}</label>
                    <textarea
                        data-testid="entry-notes-input"
                        className="bm-input"
                        rows={3}
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        data-gramm="false"
                        data-gramm_editor="false"
                        data-enable-grammarly="false"
                        spellCheck={false}
                        placeholder={f.isCruisingLog ? "Departure marina, anchorages visited, weather, crew, highlights…" : ""}
                    />
                </div>

                {f.isCruisingLog && (
                    <div data-testid="entry-location-section" style={{ marginBottom: 14 }}>
                        <label className="bm-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <MapPin size={12} style={{ color: "var(--bm-gold)" }} />
                            Location
                            <span style={{ color: "var(--bm-text-dim)", fontWeight: 400, marginLeft: 4 }}>(optional)</span>
                        </label>
                        <LocationCapture
                            value={form.lat != null && form.lng != null ? { lat: form.lat, lng: form.lng, place_name: form.place_name } : null}
                            onChange={(loc) => setForm((s) => ({
                                ...s,
                                lat: loc?.lat ?? null,
                                lng: loc?.lng ?? null,
                                place_name: loc?.place_name ?? null,
                            }))}
                        />
                    </div>
                )}

                {f.isCruisingLog && (
                    <div data-testid="entry-encounters-section" style={{ marginBottom: 14 }}>
                        <label className="bm-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <Anchor size={12} style={{ color: "var(--bm-gold)" }} />
                            Boats we crossed paths with
                            <span style={{ color: "var(--bm-text-dim)", fontWeight: 400, marginLeft: 4 }}>(optional)</span>
                        </label>
                        <EncounterPicker
                            value={form.encounter_contact_ids}
                            onChange={(ids) => setForm((s) => ({ ...s, encounter_contact_ids: ids }))}
                            data-testid="entry-encounter-picker"
                        />
                    </div>
                )}

                {err && <div style={{ color: "var(--bm-bad)", marginBottom: 12, fontSize: 13 }}>{err}</div>}

                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <button data-testid="entry-cancel-btn" className="bm-btn bm-btn-ghost" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        data-testid="entry-save-btn"
                        className="bm-btn bm-btn-gold"
                        disabled={busy || (!form.title && !f.isCruisingLog)}
                        onClick={save}
                    >
                        {busy ? "Saving…" : isRef ? "Save part" : "Save entry"}
                    </button>
                </div>
            </div>

            {lightboxIdx >= 0 && form.photos[lightboxIdx] && !isPdfDataUri(form.photos[lightboxIdx].data) && (
                <div
                    data-testid="entry-photo-fullscreen"
                    onClick={() => setLightboxIdx(-1)}
                    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", display: "grid", placeItems: "center", zIndex: 200, cursor: "zoom-out" }}
                >
                    <img src={form.photos[lightboxIdx].data} alt="Receipt full" style={{ maxWidth: "94vw", maxHeight: "94vh", objectFit: "contain" }} />
                </div>
            )}
        </div>
    );
}

/* ---------------- Sub-components ---------------- */

function ReferenceFields({ form, setForm }) {
    return (
        <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                    <label className="bm-label">OEM #</label>
                    <input data-testid="entry-oem-input" className="bm-input" value={form.oem_number} onChange={(e) => setForm({ ...form, oem_number: e.target.value })} placeholder="e.g. 471034" />
                </div>
                <div>
                    <label className="bm-label">Aftermarket #</label>
                    <input data-testid="entry-aftermarket-input" className="bm-input" value={form.aftermarket_number} onChange={(e) => setForm({ ...form, aftermarket_number: e.target.value })} placeholder="e.g. Racor R24S" />
                </div>
            </div>
            <div style={{ marginBottom: 12 }}>
                <label className="bm-label">Supplier</label>
                <input data-testid="entry-vendor-input" className="bm-input" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="e.g. Volvo Dealer" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 12 }}>
                <div>
                    <label className="bm-label">Unit Cost</label>
                    <div style={{ display: "flex", gap: 6 }}>
                        <input data-testid="entry-amount-input" type="number" step="0.01" inputMode="decimal" className="bm-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} style={{ flex: 1, minWidth: 0 }} />
                        <select data-testid="entry-currency-select" className="bm-input" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} style={{ width: 90, paddingRight: 6, flexShrink: 0 }}>
                            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                </div>
                <div>
                    <label className="bm-label">Qty on hand</label>
                    <input data-testid="entry-quantity-input" type="number" step="1" inputMode="decimal" className="bm-input" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} style={{ width: "100%" }} />
                </div>
            </div>
        </>
    );
}

function StandardFields({ form, setForm, f }) {
    return (
        <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                    <label className="bm-label">Date</label>
                    <input data-testid="entry-date-input" type="date" className="bm-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                <div>
                    <label className="bm-label">Status</label>
                    <select data-testid="entry-status-select" className="bm-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                        <option value="">—</option>
                        <option value="done">Done</option>
                        <option value="scheduled">Scheduled</option>
                    </select>
                </div>
            </div>
            {form.category === "Fuel" ? (
                <FuelCostFields form={form} setForm={setForm} />
            ) : (
                <div style={{ display: "grid", gridTemplateColumns: f.showAmount && f.showQuantity ? "repeat(auto-fit, minmax(160px, 1fr))" : "1fr", gap: 12, marginBottom: 12 }}>
                    {f.showAmount && (
                        <div>
                            <label className="bm-label">{f.amountLabel}</label>
                            <div style={{ display: "flex", gap: 6 }}>
                                <input data-testid="entry-amount-input" type="number" step="0.01" inputMode="decimal" className="bm-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} style={{ flex: 1, minWidth: 0 }} />
                                <select data-testid="entry-currency-select" className="bm-input" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} style={{ width: 90, paddingRight: 6, flexShrink: 0 }}>
                                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                        </div>
                    )}
                    {f.showQuantity && (
                        <div>
                            <label className="bm-label">{f.qtyLabel}</label>
                            <input data-testid="entry-quantity-input" type="number" step="0.01" className="bm-input" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                        </div>
                    )}
                </div>
            )}
            {f.showVendor && (
                <div style={{ marginBottom: 12 }}>
                    <label className="bm-label">Vendor</label>
                    <input data-testid="entry-vendor-input" className="bm-input" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
                </div>
            )}
            {f.showHours && (
                <div style={{ marginBottom: 12 }}>
                    <label className="bm-label">Engine hours at event</label>
                    <input data-testid="entry-hours-input" type="number" step="0.1" className="bm-input" value={form.hours_at_event} onChange={(e) => setForm({ ...form, hours_at_event: e.target.value })} />
                </div>
            )}
            {f.showSchedule && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <div>
                        <label className="bm-label">Due date</label>
                        <input data-testid="entry-due-at-input" type="date" className="bm-input" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} />
                    </div>
                    <div>
                        <label className="bm-label">Due at hours</label>
                        <input data-testid="entry-due-hours-input" type="number" step="0.1" className="bm-input" value={form.due_hours} onChange={(e) => setForm({ ...form, due_hours: e.target.value })} />
                    </div>
                </div>
            )}
        </>
    );
}

function CruisingLogFields({ form, setForm, trips = [] }) {
    // When both start_hours and end_hours are set, auto-compute hours_underway
    // so users don't double-enter. We update on each render that detects new
    // start/end values, keeping hours_underway in sync — but if the user
    // overrides hours_underway manually, we respect that on save (the field
    // is still editable).
    const sh = form.start_hours;
    const eh = form.end_hours;
    const computedHours = (sh !== "" && eh !== "" && Number.isFinite(parseFloat(sh)) && Number.isFinite(parseFloat(eh)) && parseFloat(eh) >= parseFloat(sh))
        ? Number((parseFloat(eh) - parseFloat(sh)).toFixed(2))
        : null;
    return (
        <>
            {/* Trip grouping — pick an existing trip or leave blank (standalone) */}
            {trips.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                    <label className="bm-label">Trip</label>
                    <select
                        data-testid="entry-trip-select"
                        className="bm-input"
                        value={form.trip_id || ""}
                        onChange={(e) => setForm({ ...form, trip_id: e.target.value })}
                    >
                        <option value="">— Standalone (no trip) —</option>
                        {trips.map((t) => (
                            <option key={t.id} value={t.id}>
                                {t.name}{t.status === "closed" ? " (closed)" : ""}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                    <label className="bm-label">Departure date</label>
                    <input
                        data-testid="entry-date-input"
                        type="date"
                        className="bm-input"
                        value={form.date}
                        onChange={(e) => setForm({ ...form, date: e.target.value })}
                    />
                </div>
                <div>
                    <label className="bm-label">Return date</label>
                    <input
                        data-testid="entry-date-to-input"
                        type="date"
                        className="bm-input"
                        value={form.date_to}
                        min={form.date || undefined}
                        onChange={(e) => setForm({ ...form, date_to: e.target.value })}
                    />
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                    <label className="bm-label">Start engine hours</label>
                    <input
                        data-testid="entry-start-hours-input"
                        type="number"
                        step="0.1"
                        min="0"
                        className="bm-input"
                        value={form.start_hours}
                        onChange={(e) => setForm({ ...form, start_hours: e.target.value })}
                        placeholder="e.g. 1681.8"
                    />
                </div>
                <div>
                    <label className="bm-label">End engine hours</label>
                    <input
                        data-testid="entry-end-hours-input"
                        type="number"
                        step="0.1"
                        min="0"
                        className="bm-input"
                        value={form.end_hours}
                        onChange={(e) => setForm({ ...form, end_hours: e.target.value })}
                        placeholder="e.g. 1696.3"
                    />
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                    <label className="bm-label">Hours underway {computedHours != null && form.hours_underway === "" ? <span style={{ color: "var(--bm-text-dim)", fontSize: 11, marginLeft: 6 }}>auto: {computedHours}</span> : null}</label>
                    <input
                        data-testid="entry-hours-underway-input"
                        type="number"
                        step="0.1"
                        min="0"
                        className="bm-input"
                        value={form.hours_underway === "" && computedHours != null ? computedHours : form.hours_underway}
                        onChange={(e) => setForm({ ...form, hours_underway: e.target.value })}
                        placeholder="e.g. 14.5"
                    />
                </div>
                <div>
                    <label className="bm-label">Distance (nm)</label>
                    <input
                        data-testid="entry-distance-nm-input"
                        type="number"
                        step="0.1"
                        min="0"
                        className="bm-input"
                        value={form.distance_nm}
                        onChange={(e) => setForm({ ...form, distance_nm: e.target.value })}
                        placeholder="e.g. 84"
                    />
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                    <label className="bm-label">Fuel added</label>
                    <input
                        data-testid="entry-log-fuel-input"
                        type="number"
                        step="0.1"
                        min="0"
                        className="bm-input"
                        value={form.quantity}
                        onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                        placeholder="e.g. 42"
                    />
                </div>
                <div>
                    <label className="bm-label">Fuel unit</label>
                    <select
                        data-testid="entry-log-fuel-unit-select"
                        className="bm-input"
                        value={form.unit || "gal"}
                        onChange={(e) => setForm({ ...form, unit: e.target.value })}
                    >
                        <option value="gal">Gallons (US)</option>
                        <option value="L">Litres</option>
                    </select>
                </div>
            </div>
            {form.distance_nm !== "" && (form.hours_underway !== "" || computedHours != null) && (
                () => {
                    const huNum = form.hours_underway !== "" ? parseFloat(form.hours_underway) : computedHours;
                    if (!huNum || huNum <= 0) return null;
                    return (
                        <div data-testid="entry-avg-speed" style={{
                            fontSize: 12, color: "var(--bm-text-muted)",
                            marginTop: -4, marginBottom: 12,
                            fontVariantNumeric: "tabular-nums",
                        }}>
                            Average speed: <strong style={{ color: "var(--bm-gold)" }}>
                                {(parseFloat(form.distance_nm) / huNum).toFixed(1)}
                            </strong> knots
                        </div>
                    );
                }
            )()}
        </>
    );
}

function PhotoSection({ form, photoBusy, setLightboxIdx, onPhotosPicked, removePhoto, canOcr, isPro = true, ocrBusy, ocrNote, extractWithAi }) {
    const label = form.category === "Receipts"
        ? "Receipt photos or PDF"
        : form.category === "Surveys"
            ? "Survey photos or PDF"
            : "Photos or PDF";
    const photos = form.photos || [];
    const canAddMore = photos.length < MAX_PHOTOS;
    return (
        <div style={{ marginBottom: 14 }}>
            <label className="bm-label">
                {label}
                <span style={{ color: "var(--bm-text-muted)", fontWeight: 400, marginLeft: 8 }}>
                    · {photos.length} / {MAX_PHOTOS}
                </span>
            </label>

            {photos.length > 0 && (
                <div
                    data-testid="entry-photo-gallery"
                    style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 8, marginBottom: 10 }}
                >
                    {photos.map((p, idx) => {
                        const isPdf = isPdfDataUri(p.data);
                        // Stable key: prefer filename, fall back to a short hash
                        // of the data URI so deleting a middle photo doesn't
                        // remount unrelated thumbnails.
                        const key = p.filename || (p.data ? `data-${p.data.slice(-24)}` : `photo-${idx}`);
                        return (
                            <div
                                key={key}
                                data-testid={`entry-photo-thumb-${idx}`}
                                style={{ position: "relative", aspectRatio: "1 / 1", borderRadius: "var(--bm-r)", border: "1px solid var(--bm-border-soft)", overflow: "hidden", background: isPdf ? "rgba(91,185,232,0.06)" : "#000" }}
                            >
                                {isPdf ? (
                                    <button
                                        type="button"
                                        onClick={(ev) => {
                                            ev.preventDefault();
                                            openPdfDataUri(p.data, p.filename || "document.pdf");
                                        }}
                                        style={{ width: "100%", height: "100%", background: "transparent", border: "none", color: "var(--bm-text)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, padding: 6, cursor: "pointer", textAlign: "center" }}
                                    >
                                        <FileText size={26} color="var(--bm-blue)" />
                                        <div style={{ fontSize: 10, color: "var(--bm-text-muted)", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%", whiteSpace: "nowrap" }}>
                                            {p.filename || "PDF"}
                                        </div>
                                        <div style={{ fontSize: 9, color: "var(--bm-text-dim)" }}>Tap to open</div>
                                    </button>
                                ) : (
                                    <img
                                        src={p.data}
                                        alt={`attachment ${idx + 1}`}
                                        onClick={() => setLightboxIdx(idx)}
                                        style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "zoom-in", display: "block" }}
                                    />
                                )}
                                <button
                                    type="button"
                                    data-testid={`entry-photo-remove-${idx}`}
                                    onClick={() => removePhoto(idx)}
                                    style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.7)", color: "var(--bm-text)", border: "none", borderRadius: 999, width: 22, height: 22, display: "grid", placeItems: "center", cursor: "pointer" }}
                                    aria-label="Remove attachment"
                                >
                                    <X size={11} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {canAddMore && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <label data-testid="entry-camera-label" className="bm-btn bm-btn-primary" style={{ cursor: "pointer" }}>
                        <Camera size={15} /> {photoBusy ? "Processing…" : (photos.length ? "Add another" : "Use camera")}
                        <input
                            data-testid="entry-camera-input"
                            type="file"
                            accept="image/*"
                            capture="environment"
                            style={{ display: "none" }}
                            onChange={(e) => { onPhotosPicked(e.target.files); e.target.value = ""; }}
                        />
                    </label>
                    <label data-testid="entry-upload-label" className="bm-btn bm-btn-primary" style={{ cursor: "pointer" }}>
                        <ImageIcon size={15} /> Choose from gallery
                        <input
                            data-testid="entry-upload-input"
                            type="file"
                            accept="image/*,application/pdf"
                            multiple
                            style={{ display: "none" }}
                            onChange={(e) => { onPhotosPicked(e.target.files); e.target.value = ""; }}
                        />
                    </label>
                </div>
            )}

            {canOcr && (
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                    <button
                        type="button"
                        data-testid="entry-ai-extract-btn"
                        className="bm-btn bm-btn-ghost"
                        onClick={isPro ? extractWithAi : () => goUpgrade()}
                        disabled={ocrBusy}
                        style={{ alignSelf: "flex-start", padding: "8px 14px", fontSize: 13 }}
                        title={isPro ? undefined : "AI extraction is part of Bosun's Mate Pro"}
                    >
                        <Sparkles size={14} /> {isPro ? (ocrBusy ? "Reading receipt…" : "Extract details with AI") : "Extract details with AI — Pro"}
                    </button>
                    {ocrNote && (
                        <div data-testid="entry-ai-note" style={{ fontSize: 12, color: ocrNote.startsWith("AI filled") ? "var(--bm-good)" : "var(--bm-text-muted)" }}>
                            {ocrNote}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function CustomFieldsSection({ category, customFields, customData, setCustom }) {
    return (
        <div
            data-testid="custom-fields-section"
            style={{ marginBottom: 14, paddingTop: 8, borderTop: "1px solid var(--bm-border-soft)" }}
        >
            <div className="bm-label" style={{ marginTop: 12, marginBottom: 8, color: "var(--bm-gold)", fontSize: 11, letterSpacing: "0.18em" }}>
                CUSTOM · {category}
            </div>
            {customFields.map((cf) => {
                const val = customData[cf.key] ?? "";
                const tid = `cf-input-${cf.key}`;
                return (
                    <div key={cf.key} style={{ marginBottom: 10 }}>
                        <label className="bm-label">{cf.label}</label>
                        {cf.type === "select" ? (
                            <select data-testid={tid} className="bm-input" value={val} onChange={(e) => setCustom(cf.key, e.target.value)}>
                                <option value="">—</option>
                                {(cf.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                        ) : (
                            <input
                                data-testid={tid}
                                className="bm-input"
                                type={cf.type === "number" ? "number" : cf.type === "date" ? "date" : "text"}
                                step={cf.type === "number" ? "any" : undefined}
                                value={val}
                                onChange={(e) => setCustom(cf.key, e.target.value)}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}


/**
 * FuelCostFields — three-field layout that prevents the unit-price-as-total
 * confusion that caused the $25B YTD-cost bug.
 *
 * Layout: Volume × Unit price = Total cost (auto-computed).
 *  - User edits Volume or Unit Price → if Total is empty or matches the
 *    previous auto-compute, we recompute Total on the fly.
 *  - User edits Total directly → we leave it alone (manual override wins),
 *    but show a sanity warning if it disagrees with Volume × Unit Price.
 *  - Currency selector lives next to Total since that's the dollar field.
 */
function FuelCostFields({ form, setForm }) {
    const q = parseFloat(form.quantity);
    const up = parseFloat(form.unit_price);
    const amt = parseFloat(form.amount);
    const computed = !isNaN(q) && !isNaN(up) ? +(q * up).toFixed(2) : null;

    const updateField = (key, val) => {
        setForm((s) => {
            const next = { ...s, [key]: val };
            // Re-derive `amount` whenever the user edits quantity or unit_price,
            // but ONLY if amount is empty OR matches the previously-computed
            // value (i.e. user hasn't manually overridden it).
            if (key === "quantity" || key === "unit_price") {
                const nQ = parseFloat(key === "quantity" ? val : next.quantity);
                const nUp = parseFloat(key === "unit_price" ? val : next.unit_price);
                if (!isNaN(nQ) && !isNaN(nUp)) {
                    const newTotal = +(nQ * nUp).toFixed(2);
                    const currentAmount = parseFloat(s.amount);
                    const prevComputed = !isNaN(q) && !isNaN(up) ? +(q * up).toFixed(2) : null;
                    if (
                        s.amount === "" ||
                        s.amount == null ||
                        (prevComputed !== null && !isNaN(currentAmount) && Math.abs(currentAmount - prevComputed) < 0.01)
                    ) {
                        next.amount = String(newTotal);
                    }
                }
            }
            return next;
        });
    };

    // Sanity warning: user-entered Total differs from Volume × Unit Price by > 10%
    const showMismatchWarn = (
        !isNaN(amt) && computed !== null
        && Math.abs(amt - computed) / Math.max(computed, 0.01) > 0.1
        && Math.abs(amt - computed) > 1.0  // ignore tiny rounding gaps
    );

    return (
        <div data-testid="fuel-cost-fields" style={{ marginBottom: 12 }}>
            {/* Responsive 3-column grid — wraps to 1 column on narrow viewports
                (mobile PWA / native) so each field gets full width and every
                digit typed stays visible. Previously fixed at 3 cols regardless
                of viewport, which cramped the input on iPhone-sized screens. */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 8 }}>
                <div>
                    <label className="bm-label">Volume</label>
                    <div style={{ display: "flex", gap: 4 }}>
                        <input
                            data-testid="entry-quantity-input"
                            type="number"
                            step="0.01"
                            inputMode="decimal"
                            className="bm-input"
                            value={form.quantity}
                            onChange={(e) => updateField("quantity", e.target.value)}
                            placeholder="25"
                            style={{ flex: 1, minWidth: 0 }}
                        />
                        <select
                            data-testid="entry-unit-select"
                            className="bm-input"
                            value={form.unit || "gal"}
                            onChange={(e) => setForm((s) => ({ ...s, unit: e.target.value }))}
                            style={{ width: 72, paddingRight: 4, flexShrink: 0 }}
                        >
                            <option value="gal">gal</option>
                            <option value="USG">USG</option>
                            <option value="L">L</option>
                        </select>
                    </div>
                </div>
                <div>
                    <label className="bm-label">Unit price</label>
                    <input
                        data-testid="entry-unit-price-input"
                        type="number"
                        step="0.0001"
                        inputMode="decimal"
                        className="bm-input"
                        value={form.unit_price}
                        onChange={(e) => updateField("unit_price", e.target.value)}
                        placeholder="5.31"
                        style={{ width: "100%" }}
                    />
                </div>
                <div>
                    <label className="bm-label">Total cost</label>
                    <div style={{ display: "flex", gap: 6 }}>
                        <input
                            data-testid="entry-amount-input"
                            type="number"
                            step="0.01"
                            inputMode="decimal"
                            className="bm-input"
                            value={form.amount}
                            onChange={(e) => setForm((s) => ({ ...s, amount: e.target.value }))}
                            placeholder={computed != null ? computed.toFixed(2) : "132.75"}
                            style={{ flex: 1, minWidth: 0 }}
                        />
                        <select
                            data-testid="entry-currency-select"
                            className="bm-input"
                            value={form.currency}
                            onChange={(e) => setForm((s) => ({ ...s, currency: e.target.value }))}
                            style={{ width: 82, paddingRight: 4, flexShrink: 0 }}
                        >
                            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            {/* Live breakdown / hint */}
            {computed !== null && !showMismatchWarn && (
                <p data-testid="fuel-breakdown" style={{ margin: "2px 0 0", fontSize: 11, color: "var(--bm-text-dim)" }}>
                    {q} {form.unit || "gal"} × ${up.toFixed(4)} = <strong style={{ color: "var(--bm-text)" }}>${computed.toFixed(2)}</strong>
                </p>
            )}

            {/* Sanity warning when the manually-entered total disagrees materially */}
            {showMismatchWarn && (
                <p data-testid="fuel-mismatch-warning" style={{
                    margin: "6px 0 0", fontSize: 12,
                    color: "#e76f51",
                    padding: "8px 10px",
                    background: "rgba(231, 111, 81, 0.08)",
                    border: "1px solid rgba(231, 111, 81, 0.4)",
                    borderRadius: 6,
                }}>
                    ⚠ Total cost (${amt.toFixed(2)}) doesn&apos;t match Volume × Unit price (= ${computed.toFixed(2)}).
                    Double-check — did you mean to enter <strong>${computed.toFixed(2)}</strong>?
                </p>
            )}
        </div>
    );
}
